import React, { useState, useEffect } from "react";
import { 
    Card, Table, Row, Col, Button, Spinner, Alert, Badge,
    Form, Modal, InputGroup, OverlayTrigger, Tooltip, Pagination
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { formatDate } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";

export default function Utilisateurs() {
    const navigate = useNavigate();
    const [utilisateurs, setUtilisateurs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState('add');
    const [currentUser, setCurrentUser] = useState(null);
    const [formData, setFormData] = useState({
        nom_utilisateur: '',
        prenom_utilisateur: '',
        email: '',
        role: 'commercial',
        mot_de_passe: '',
        actif: true
    });
    const [saving, setSaving] = useState(false);
    const [success, setSuccess] = useState('');
    const [formError, setFormError] = useState('');

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    useEffect(() => {
        fetchUtilisateurs();
    }, []);

    const fetchUtilisateurs = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/parametres.php?action=utilisateurs`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Erreur chargement");
            }
            
            const result = await response.json();
            setUtilisateurs(result.data || []);
            setCurrentPage(1);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = utilisateurs.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(utilisateurs.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const renderPagination = () => {
        let items = [];
        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);

        if (endPage - startPage + 1 < maxVisible) {
            startPage = Math.max(1, endPage - maxVisible + 1);
        }

        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
                    1
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
            }
        }

        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item 
                    key={number} 
                    active={number === currentPage}
                    onClick={() => handlePageChange(number)}
                >
                    {number}
                </Pagination.Item>
            );
        }

        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
            }
            items.push(
                <Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>
                    {totalPages}
                </Pagination.Item>
            );
        }

        return items;
    };

    const handleAdd = () => {
        setModalMode('add');
        setCurrentUser(null);
        setFormData({
            nom_utilisateur: '',
            prenom_utilisateur: '',
            email: '',
            role: 'commercial',
            mot_de_passe: '',
            actif: true
        });
        setFormError('');
        setShowModal(true);
    };

    const handleEdit = (user) => {
        setModalMode('edit');
        setCurrentUser(user);
        setFormData({
            nom_utilisateur: user.nom_utilisateur,
            prenom_utilisateur: user.prenom_utilisateur,
            email: user.email,
            role: user.role,
            mot_de_passe: '',
            actif: user.actif === 1 || user.actif === true
        });
        setFormError('');
        setShowModal(true);
    };

    const handleDeleteClick = (user) => {
        setUserToDelete(user);
        setShowDeleteModal(true);
    };

    const handleConfirmDelete = async () => {
        if (!userToDelete) return;
        
        setIsDeleting(true);
        setError(null);
        
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/parametres.php?action=delete_utilisateur&id=${userToDelete.id_utilisateur}`,
                {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );
            
            const result = await response.json();
            
            if (result.success) {
                setShowDeleteModal(false);
                setUserToDelete(null);
                setSuccess("Utilisateur supprimé avec succès");
                setTimeout(() => setSuccess(''), 3000);
                fetchUtilisateurs();
            } else {
                setError(result.message || "Erreur lors de la suppression");
            }
        } catch (err) {
            setError("Erreur: " + err.message);
        } finally {
            setIsDeleting(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value
        });
    };

    const handleSubmit = async () => {
        if (!formData.nom_utilisateur || !formData.prenom_utilisateur || !formData.email) {
            setFormError("Veuillez remplir tous les champs obligatoires");
            return;
        }

        if (modalMode === 'add' && !formData.mot_de_passe) {
            setFormError("Le mot de passe est requis pour un nouvel utilisateur");
            return;
        }

        if (modalMode === 'add' && formData.mot_de_passe.length < 6) {
            setFormError("Le mot de passe doit contenir au moins 6 caractères");
            return;
        }

        setSaving(true);
        setFormError('');
        setError(null);
        
        try {
            const token = localStorage.getItem("token");
            
            let url = `${API_BASE_URL}/parametres.php`;
            let method = 'POST';
            let body = {};
            
            if (modalMode === 'add') {
                body = {
                    action: 'create_utilisateur',
                    ...formData
                };
            } else {
                url = `${API_BASE_URL}/parametres.php?action=update_utilisateur&id=${currentUser.id_utilisateur}`;
                method = 'PUT';
                body = {
                    nom_utilisateur: formData.nom_utilisateur,
                    prenom_utilisateur: formData.prenom_utilisateur,
                    email: formData.email,
                    role: formData.role,
                    actif: formData.actif
                };
            }
            
            const response = await fetch(url, {
                method: method,
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(body)
            });
            
            const result = await response.json();
            
            if (result.success) {
                setShowModal(false);
                setSuccess(modalMode === 'add' ? "Utilisateur créé avec succès" : "Utilisateur modifié avec succès");
                setTimeout(() => setSuccess(''), 3000);
                fetchUtilisateurs();
            } else {
                setFormError(result.message || "Erreur lors de l'opération");
            }
        } catch (err) {
            setFormError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const getRoleBadge = (role) => {
        const configs = {
            admin: { color: '#ef4444', icon: 'ri-shield-user-line', label: 'Administrateur' },
            gestionnaire: { color: '#f59e0b', icon: 'ri-user-settings-line', label: 'Gestionnaire' },
            commercial: { color: '#0ea5e9', icon: 'ri-user-star-line', label: 'Commercial' },
            magasinier: { color: '#64748b', icon: 'ri-user-line', label: 'Magasinier' }
        };
        const config = configs[role] || configs.commercial;
        return (
            <span className="status-badge" style={{ background: `${config.color}15`, color: config.color }}>
                <i className={`${config.icon} me-1 fs-10`}></i>
                {config.label}
            </span>
        );
    };

    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center">
                            <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem', color: '#21ACCD' }} role="status">
                                <span className="visually-hidden">Chargement...</span>
                            </div>
                            <h5 className="text-muted">Chargement des utilisateurs...</h5>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">
                    {/* En-tête simplifié */}
                    <div className="d-md-flex align-items-center justify-content-between mb-4">
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <div className="p-2 bg-primary bg-opacity-10 rounded-3">
                                    <i className="ri-user-settings-line fs-20 text-primary" style={{ color: '#21ACCD' }}></i>
                                </div>
                                <div>
                                    <h4 className="main-title mb-0 fw-bold">Utilisateurs</h4>
                                    <p className="text-muted small mb-0">
                                        Gérez les accès à l'application • {utilisateurs.length} utilisateur(s)
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 mt-3 mt-md-0">
                            <Button 
                                variant="primary" 
                                onClick={handleAdd}
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                                size="sm"
                                style={{ background: '#21ACCD', border: 'none' }}
                            >
                                <i className="ri-add-line"></i>
                                Nouvel utilisateur
                            </Button>
                            <Button 
                                variant="outline-secondary" 
                                onClick={() => navigate('/parametres')}
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                                size="sm"
                            >
                                <i className="ri-arrow-left-line"></i>
                                Retour
                            </Button>
                        </div>
                    </div>

                    {/* Alertes modernes */}
                    {success && (
                        <Alert variant="success" onClose={() => setSuccess('')} dismissible className="mb-4 alert-modern">
                            <i className="ri-checkbox-circle-line me-2 fs-5"></i>
                            {success}
                        </Alert>
                    )}
                    
                    {error && (
                        <Alert variant="danger" onClose={() => setError(null)} dismissible className="mb-4 alert-modern">
                            <i className="ri-error-warning-line me-2 fs-5"></i>
                            {error}
                        </Alert>
                    )}

                    {/* Tableau avec pagination */}
                    <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
                        <Card.Header className="bg-transparent border-0 pt-4 pb-3">
                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                <div className="d-flex align-items-center gap-2">
                                    <div className="section-icon-small">
                                        <i className="ri-group-line text-primary"></i>
                                    </div>
                                    <h6 className="mb-0 fw-semibold">Liste des utilisateurs</h6>
                                </div>
                                <Badge className="px-3 py-2 rounded-pill" style={{ background: '#21ACCD' }}>
                                    {utilisateurs.length} utilisateur(s)
                                </Badge>
                            </div>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="align-middle mb-0 custom-table">
                                    <thead className="bg-light">
                                        <tr>
                                            <th className="ps-4">Utilisateur</th>
                                            <th>Email</th>
                                            <th>Rôle</th>
                                            <th>Statut</th>
                                            <th>Dernière connexion</th>
                                            <th className="text-center pe-4">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentItems.length > 0 ? (
                                            currentItems.map((user, index) => (
                                                <tr key={user.id_utilisateur} className="user-row">
                                                    <td className="ps-4">
                                                        <div className="d-flex align-items-center gap-2">
                                                            <div className="avatar-icon">
                                                                <i className="ri-user-3-line"></i>
                                                            </div>
                                                            <div>
                                                                <strong>{user.prenom_utilisateur} {user.nom_utilisateur}</strong>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <span className="d-flex align-items-center gap-1">
                                                            <i className="ri-mail-line text-muted"></i>
                                                            {user.email}
                                                        </span>
                                                    </td>
                                                    <td>{getRoleBadge(user.role)}</td>
                                                    <td>
                                                        {user.actif ? (
                                                            <span className="status-badge" style={{ background: '#10b98115', color: '#10b981' }}>
                                                                <i className="ri-check-line me-1 fs-10"></i>
                                                                Actif
                                                            </span>
                                                        ) : (
                                                            <span className="status-badge" style={{ background: '#64748b15', color: '#64748b' }}>
                                                                <i className="ri-close-line me-1 fs-10"></i>
                                                                Inactif
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        {user.derniere_connexion ? (
                                                            <span className="d-flex align-items-center gap-1">
                                                                <i className="ri-calendar-line text-muted"></i>
                                                                {formatDate(user.derniere_connexion)}
                                                            </span>
                                                        ) : <span className="text-muted">-</span>}
                                                    </td>
                                                    <td className="text-center pe-4">
                                                        <div className="d-flex gap-2 justify-content-center">
                                                            <OverlayTrigger placement="top" overlay={<Tooltip>Modifier</Tooltip>}>
                                                                <Button 
                                                                    variant="outline-primary" 
                                                                    size="sm"
                                                                    className="action-btn"
                                                                    onClick={() => handleEdit(user)}
                                                                >
                                                                    <i className="ri-pencil-line"></i>
                                                                </Button>
                                                            </OverlayTrigger>
                                                            <OverlayTrigger placement="top" overlay={<Tooltip>Supprimer</Tooltip>}>
                                                                <Button 
                                                                    variant="outline-danger" 
                                                                    size="sm"
                                                                    className="action-btn"
                                                                    onClick={() => handleDeleteClick(user)}
                                                                >
                                                                    <i className="ri-delete-bin-line"></i>
                                                                </Button>
                                                            </OverlayTrigger>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="6" className="text-center py-5">
                                                    <i className="ri-user-line fs-48 text-muted mb-3 d-block"></i>
                                                    <h5>Aucun utilisateur trouvé</h5>
                                                    <p className="text-muted">Commencez par créer un nouvel utilisateur</p>
                                                    <Button 
                                                        variant="primary" 
                                                        onClick={handleAdd}
                                                        className="rounded-pill"
                                                        style={{ background: '#21ACCD', border: 'none' }}
                                                    >
                                                        <i className="ri-add-line me-2"></i>
                                                        Nouvel utilisateur
                                                    </Button>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="d-flex justify-content-between align-items-center p-3 border-top">
                                    <div className="text-muted small">
                                        <i className="ri-file-list-line me-1"></i>
                                        Affichage de {indexOfFirstItem + 1} à {Math.min(indexOfLastItem, utilisateurs.length)} sur {utilisateurs.length} utilisateurs
                                    </div>
                                    <Pagination className="mb-0">
                                        <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                                        {renderPagination()}
                                        <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
                                    </Pagination>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </div>

                {/* Modal d'ajout/modification modernisé */}
                <Modal show={showModal} onHide={() => setShowModal(false)} centered className="modern-modal">
                    <Modal.Header closeButton className="border-0 pb-0" style={{ background: '#21ACCD' }}>
                        <Modal.Title className="w-100">
                            <div className="text-center">
                                <div className="confirm-icon mb-3">
                                    <i className={`${modalMode === 'add' ? 'ri-user-add-line' : 'ri-pencil-line'} text-white`} style={{ fontSize: '4rem' }}></i>
                                </div>
                                <h3 className="fw-bold text-white">
                                    {modalMode === 'add' ? 'Nouvel utilisateur' : 'Modifier l\'utilisateur'}
                                </h3>
                            </div>
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="pt-4">
                        {formError && (
                            <Alert variant="danger" className="mb-4 rounded-3">
                                <i className="ri-error-warning-line me-2"></i>
                                {formError}
                            </Alert>
                        )}
                        
                        <Form>
                            <Row className="g-3">
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label className="fw-semibold">Prénom</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="prenom_utilisateur"
                                            value={formData.prenom_utilisateur}
                                            onChange={handleChange}
                                            placeholder="Jean"
                                            required
                                            className="border-2 rounded-3"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label className="fw-semibold">Nom</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="nom_utilisateur"
                                            value={formData.nom_utilisateur}
                                            onChange={handleChange}
                                            placeholder="Dupont"
                                            required
                                            className="border-2 rounded-3"
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                            
                            <Form.Group className="mt-3">
                                <Form.Label className="fw-semibold">Email</Form.Label>
                                <InputGroup>
                                    <InputGroup.Text className="bg-light border-2">
                                        <i className="ri-mail-line"></i>
                                    </InputGroup.Text>
                                    <Form.Control
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        placeholder="jean.dupont@exemple.com"
                                        required
                                        className="border-2"
                                    />
                                </InputGroup>
                            </Form.Group>
                            
                            {modalMode === 'add' && (
                                <Form.Group className="mt-3">
                                    <Form.Label className="fw-semibold">Mot de passe</Form.Label>
                                    <InputGroup>
                                        <InputGroup.Text className="bg-light border-2">
                                            <i className="ri-lock-line"></i>
                                        </InputGroup.Text>
                                        <Form.Control
                                            type="password"
                                            name="mot_de_passe"
                                            value={formData.mot_de_passe}
                                            onChange={handleChange}
                                            placeholder="••••••••"
                                            required
                                            className="border-2"
                                        />
                                    </InputGroup>
                                </Form.Group>
                            )}
                            
                            <Form.Group className="mt-3">
                                <Form.Label className="fw-semibold">Rôle</Form.Label>
                                <Form.Select name="role" value={formData.role} onChange={handleChange} className="border-2 rounded-3">
                                    <option value="admin">Administrateur</option>
                                    <option value="gestionnaire">Gestionnaire</option>
                                    <option value="commercial">Commercial</option>
                                    <option value="magasinier">Magasinier</option>
                                </Form.Select>
                            </Form.Group>
                            
                            <Form.Group className="mt-3">
                                <Form.Check
                                    type="switch"
                                    id="actif"
                                    name="actif"
                                    label="Compte actif"
                                    checked={formData.actif}
                                    onChange={handleChange}
                                    className="fw-semibold"
                                />
                            </Form.Group>
                        </Form>
                    </Modal.Body>
                    <Modal.Footer className="border-0 pt-0 pb-4 justify-content-center">
                        <Button 
                            variant="outline-secondary" 
                            onClick={() => setShowModal(false)}
                            className="rounded-pill px-4"
                        >
                            Annuler
                        </Button>
                        <Button 
                            variant="primary" 
                            onClick={handleSubmit} 
                            disabled={saving}
                            className="rounded-pill px-5"
                            style={{ background: '#21ACCD', border: 'none' }}
                        >
                            {saving ? (
                                <>
                                    <Spinner size="sm" className="me-2" />
                                    Traitement...
                                </>
                            ) : (
                                <>
                                    <i className="ri-check-line me-2"></i>
                                    {modalMode === 'add' ? 'Créer' : 'Modifier'}
                                </>
                            )}
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* Modal de suppression modernisé */}
                <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered className="modern-modal">
                    <Modal.Header closeButton className="border-0 pb-0" style={{ background: '#dc2626' }}>
                        <Modal.Title className="w-100">
                            <div className="text-center">
                                <div className="confirm-icon mb-3">
                                    <i className="ri-delete-bin-line text-white" style={{ fontSize: '4rem' }}></i>
                                </div>
                                <h3 className="fw-bold text-white">Supprimer l'utilisateur</h3>
                            </div>
                        </Modal.Title>
                    </Modal.Header>
                    
                    <Modal.Body className="pt-4 text-center">
                        <p className="text-muted mb-4">
                            Êtes-vous sûr de vouloir supprimer <strong className="text-primary">{userToDelete?.prenom_utilisateur} {userToDelete?.nom_utilisateur}</strong> ?
                        </p>
                        
                        <Alert variant="warning" className="rounded-3 text-start">
                            <div className="d-flex">
                                <i className="ri-alert-line fs-4 me-3 flex-shrink-0"></i>
                                <div>
                                    <strong>Cette action est irréversible</strong>
                                    <p className="mb-0 small mt-1">
                                        La suppression entraînera la perte définitive des accès de cet utilisateur.
                                    </p>
                                </div>
                            </div>
                        </Alert>

                        <div className="bg-light p-3 rounded-3 mb-3">
                            <div className="d-flex justify-content-between mb-2">
                                <span className="text-muted">Nom complet :</span>
                                <span className="fw-semibold">{userToDelete?.prenom_utilisateur} {userToDelete?.nom_utilisateur}</span>
                            </div>
                            <div className="d-flex justify-content-between mb-2">
                                <span className="text-muted">Email :</span>
                                <span className="fw-semibold">{userToDelete?.email}</span>
                            </div>
                            <div className="d-flex justify-content-between">
                                <span className="text-muted">Rôle :</span>
                                <span>{getRoleBadge(userToDelete?.role)}</span>
                            </div>
                        </div>
                    </Modal.Body>
                    
                    <Modal.Footer className="border-0 pt-0 pb-4 justify-content-center">
                        <Button 
                            variant="outline-secondary" 
                            onClick={() => setShowDeleteModal(false)}
                            className="rounded-pill px-4"
                            disabled={isDeleting}
                        >
                            Annuler
                        </Button>
                        <Button 
                            variant="danger" 
                            onClick={handleConfirmDelete}
                            className="rounded-pill px-5"
                            disabled={isDeleting}
                        >
                            {isDeleting ? (
                                <>
                                    <Spinner size="sm" className="me-2" />
                                    Suppression...
                                </>
                            ) : (
                                <>
                                    <i className="ri-delete-bin-line me-2"></i>
                                    Supprimer
                                </>
                            )}
                        </Button>
                    </Modal.Footer>
                </Modal>

                <Footer />
            </div>

            <style>{`
                /* Status Badge */
                .status-badge {
                    display: inline-flex;
                    align-items: center;
                    padding: 0.3rem 0.8rem;
                    border-radius: 30px;
                    font-size: 0.7rem;
                    font-weight: 500;
                }
                
                /* Avatar Icon */
                .avatar-icon {
                    width: 40px;
                    height: 40px;
                    background: #e2f3f5;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #21ACCD;
                }
                .avatar-icon i {
                    font-size: 1.2rem;
                }
                
                /* Section Icon Small */
                .section-icon-small {
                    width: 32px;
                    height: 32px;
                    background: linear-gradient(135deg, #21ACCD20, #1a8ba820);
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                
                /* Action Buttons */
                .action-btn {
                    width: 32px;
                    height: 32px;
                    border-radius: 8px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s ease;
                    border: 1px solid #e2e8f0;
                    background: white;
                    color: #64748b;
                }
                .action-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                }
                .btn-outline-primary:hover {
                    background: linear-gradient(135deg, #21ACCD, #1a8ba8);
                    border-color: #21ACCD;
                    color: white;
                }
                .btn-outline-danger:hover {
                    background: linear-gradient(135deg, #ef4444, #dc2626);
                    border-color: #ef4444;
                    color: white;
                }
                
                /* Custom Table */
                .custom-table thead th {
                    background: #f8fafc;
                    color: #1e293b;
                    font-weight: 600;
                    font-size: 0.75rem;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    padding: 1rem 0.75rem;
                    border-bottom: 2px solid #e2e8f0;
                }
                
                .custom-table tbody td {
                    padding: 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                }
                
                .user-row {
                    transition: all 0.2s ease;
                }
                
                .user-row:hover {
                    background: #f8fafc;
                }
                
                /* Pagination */
                .pagination {
                    gap: 5px;
                }
                .pagination .page-item .page-link {
                    border-radius: 8px;
                    border: 1px solid #e2e8f0;
                    color: #1e293b;
                    padding: 0.5rem 1rem;
                    transition: all 0.2s ease;
                    font-weight: 500;
                }
                .pagination .page-item.active .page-link {
                    background: linear-gradient(135deg, #21ACCD, #1a8ba8);
                    border-color: #21ACCD;
                    color: white;
                    box-shadow: 0 4px 10px rgba(33, 172, 205, 0.3);
                }
                .pagination .page-item .page-link:hover {
                    background: #f1f5f9;
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                }
                .pagination .page-item.disabled .page-link {
                    opacity: 0.5;
                    pointer-events: none;
                }
                
                /* Modern Modal */
                .modern-modal .modal-content {
                    border-radius: 20px;
                    border: none;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.2);
                    overflow: hidden;
                }
                .confirm-icon {
                    animation: scaleIn 0.5s ease-out;
                }
                @keyframes scaleIn {
                    0% { transform: scale(0); opacity: 0; }
                    50% { transform: scale(1.2); }
                    100% { transform: scale(1); opacity: 1; }
                }
                
                .alert-modern {
                    border: none;
                    border-radius: 12px;
                    padding: 1rem 1.5rem;
                    box-shadow: 0 4px 8px rgba(0,0,0,0.05);
                }
                
                .border-2 {
                    border-width: 2px !important;
                    border-color: #e2e8f0;
                }
                .border-2:focus {
                    border-color: #21ACCD;
                    box-shadow: 0 0 0 3px rgba(33, 172, 205, 0.1);
                }
                
                .rounded-3 {
                    border-radius: 12px !important;
                }
                
                .rounded-pill {
                    border-radius: 50px !important;
                }
                
                .fs-10 {
                    font-size: 0.625rem;
                }
                .fs-20 {
                    font-size: 1.25rem;
                }
                .fs-48 {
                    font-size: 3rem;
                }
            `}</style>
        </>
    );
}